import React, { useCallback, useMemo } from 'react'
import './style/index.less'
import classNames from 'classnames'
import {
  processImageByAli,
  processVideoByAli,
  processImageByQiniu,
  processVideoByQiniu,
} from './lib/imageUtil'
import { ImageType, CDNType, ImageOptions, Map } from './lib/type'

interface ImageProps {
  src: string
  options?: Array<ImageOptions>
  type?: ImageType
  className?: string
  style?: React.CSSProperties
  onError?: (err: any) => void
}

const CDN: Map = {
  '.poizon.com': 'ali',
  'du.hupucdn.com': 'qiniu',
}

const getHost = (src: string): CDNType => {
  if (src.indexOf('.poizon.com') > -1) {
    return 'ali'
  }
  if (src.indexOf('du.hupucdn.com') > -1) {
    return 'qiniu'
  }
  return ''
}

const Image: React.FC<ImageProps> = (props) => {
  const { src, className, style, type, options, onError, ...rest } = props
  let cdn: CDNType = ''
  let isGif = false

  const transformSrc = useCallback(
    (src: string, options?: Array<ImageOptions>) => {
      // 检查传入的options
      if (Object.prototype.toString.call(options) !== '[object Array]') return src

      if (options!.length === 0) return src

      // 获取图片的cdn厂商
      cdn = getHost(src)

      if (!cdn) return src

      // 删除query
      src = src.replace(/\?.*/, '')

      // 处理图片
      isGif = src.toLowerCase().endsWith('.gif')
      src += processSrc(cdn)

      return src
    },
    [options, src, cdn]
  )

  const processSrc = useCallback(
    (cdn) => {
      let q = ''
      if (cdn === 'ali') {
        switch (type) {
          case 'image':
            q += '?x-oss-process=image'
            q += processImageByAli(options as Array<ImageOptions>, isGif)
            break
          case 'video':
            q += '?x-oss-process=video'
            q += processVideoByAli(options as Array<ImageOptions>)
            break
        }
      } else if (cdn === 'qiniu') {
        switch (type) {
          case 'image':
            q += '?imageMogr2'
            q += processImageByQiniu(options as Array<ImageOptions>, isGif)
            break
          case 'video':
            q += '?vframe'
            q += processVideoByQiniu(options as Array<ImageOptions>)
            break
        }
      }
      return q
    },
    [type, options, cdn]
  )

  const errorCb = (err: any) => {
    onError && onError(err)
  }

  const classes = classNames('cat_image', className)
  const fmtSrc = useMemo(() => transformSrc(src, options), [src, options, type])
  return fmtSrc ? (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img style={style} src={fmtSrc} className={classes} onError={(err) => errorCb(err)} {...rest} />
  ) : null
}

Image.defaultProps = {
  src: '',
  type: 'image',
  options: [],
} as ImageProps

const processSrc = (cdn: any, options: any, type: any, isGif: boolean) => {
  let q = ''
  if (cdn === 'ali') {
    switch (type) {
      case 'image':
        q += '?x-oss-process=image'
        q += processImageByAli(options as Array<ImageOptions>, isGif)
        break
      case 'video':
        q += '?x-oss-process=video'
        q += processVideoByAli(options as Array<ImageOptions>)
        break
    }
  } else if (cdn === 'qiniu') {
    switch (type) {
      case 'image':
        q += '?imageMogr2'
        q += processImageByQiniu(options as Array<ImageOptions>, isGif)
        break
      case 'video':
        q += '?vframe'
        q += processVideoByQiniu(options as Array<ImageOptions>)
        break
    }
  }
  return q
}

Image.tSrc = ({ type = 'image', src, options }: any) => {
  // 检查传入的options
  if (Object.prototype.toString.call(options) !== '[object Array]') return src
  if (options!.length === 0) return src
  // 获取图片的cdn厂商
  const cdn = getHost(src)
  if (!cdn) return src
  // 删除query
  src = src.replace(/\?.*/, '')
  // 处理图片
  const isGif = src.toLowerCase().endsWith('.gif')
  src += processSrc(cdn, options, type, isGif)
  return src
}

export default Image
